/**
 * Created By Elia Rohana on 12/12/18
 */
import { uniq, uniqBy } from 'lodash';
import { Condition } from '@ispm-client/modules/bpmn-modeler/models/ruleset-condition';
import { QueryNode, Rule } from '@ispm-client/modules/query-builder/models/query-builder-config';
import { NodeCreationResult } from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';

interface Identifier {
  prefix: string;
  index: number;
}

export class RulesetUtils {

  private static readonly complicatedOperators = ['<', '>', '!=', 'or', '!', '<=', '>='];

  private static notSign: string = '!';
  public static and: string = '&&';
  public static or: string = '||';
  public static equal: string = '==';
  public static notEqual: string = '!=';

  private static extractConditionExpressionWithParser(nodeId: string, ruleset: QueryNode, ruleParse: (rule) => string,
                                                      conditionProvider: (condition) => string = RulesetUtils.defaultConditionProvider,
                                                      analyzeNegation: boolean = false, addNullCheck?: boolean): string {
    const identifier: Identifier = {
      prefix: nodeId,
      index: 1
    };
    return this.innerExtractConditionExpressionWithParser(identifier, ruleset, ruleParse, conditionProvider, analyzeNegation, addNullCheck);

  }

  private static innerExtractConditionExpressionWithParser(identifier: Identifier, ruleset: QueryNode, ruleParse: (rule) => string,
                                                      conditionProvider: (condition) => string = RulesetUtils.defaultConditionProvider,
                                                      analyzeNegation: boolean = false, addNullCheck?: boolean): string {
    const currentRules = ruleset.rules;
    const expressions = [];
    let currentExpression: string;
    let index = 0;
    for (const item of currentRules) {
      if (instanceOfRuleset(item)) {
        currentExpression = this.innerExtractConditionExpressionWithParser(identifier, item, ruleParse, conditionProvider, analyzeNegation, addNullCheck);
        currentExpression = currentExpression ? `(${currentExpression})` : '';
        if (analyzeNegation && item.negate === true) {
          currentExpression = this.notSign.concat(currentExpression);
          }
      }
      else {
        if (ruleParse(item) !== "") {
          currentExpression = `${identifier.prefix}_${identifier.index}_${ruleParse(item)}`;
          if (addNullCheck) {
            currentExpression = `(${identifier.prefix}_${identifier.index}_${item.fullName} ${RulesetUtils.notEqual} null ${RulesetUtils.and} ${currentExpression})`;
          }
          identifier.index++;
        }
      }
      if (index > 0) {
        expressions.push(conditionProvider((ruleset.condition)));
      }
      expressions.push(currentExpression);
      index++;
    }
    expressions.push();
    return expressions.join(' ');
  }

  static extractRules(nodeId: string, ruleset: QueryNode): Array<Rule> {
    const identifier: Identifier = {
      prefix: nodeId,
      index: 1
    };
    return this.innerExtractRules(identifier, ruleset);
  }

  private static innerExtractRules(identifier: Identifier, ruleset: QueryNode): Array<Rule> {
    let rules = [];
    const currentRules = ruleset.rules;
    for (const item of currentRules) {
      if (instanceOfRuleset(item)) {
        rules = [...rules, ...this.innerExtractRules(identifier, item)];
      }
      else {
        const key = `${identifier.prefix}_${identifier.index}_${item.fullName}`;
        if (key !== "") {
          rules.push(Object.assign({ key: `${identifier.prefix}_${identifier.index}_${item.fullName}` }, item));
          identifier.index++;
        }
      }
    }
    return rules;
  }

  static containsNegation(ruleset: QueryNode): boolean {
    const hasNegation: boolean = false;
    const currentRules = ruleset.rules;

    for (const item of currentRules) {
      if (item.negate === true) {
        return true;
      }
      else {
        if (instanceOfRuleset(item)) {
          if (this.containsNegation(item)) { return true; }
        }
      }
    }
    return hasNegation;
  }

  static extractRulesFields(nodeId: string, ruleset: QueryNode): Array<string> {
    const identifier: Identifier = {
      prefix: nodeId,
      index: 1
    };
    return uniq(RulesetUtils.extractFields(identifier, ruleset));
  }

  private static extractFields(identifier: Identifier, ruleset: QueryNode): Array<string> {
    let fields = [];
    const currentRules = ruleset.rules;
    for (const item of currentRules) {
      if (instanceOfRuleset(item)) {
        fields = [...fields, ...this.extractFields(identifier, item)];
      }
      else {
        fields.push(`${identifier.prefix}_${identifier.index}_${item.fullName}`);
        identifier.index++;
      }
    }
    return fields;
  }

  static containsComplicatedRule(rules: Array<Rule>): boolean {
    return rules.filter(rule => RulesetUtils.complicatedOperators.includes(rule.operator)).length > 0;
  }

  static hasComplicatedRule(ruleset: QueryNode): boolean {
    const rules = RulesetUtils.extractRules('dummy', ruleset);
    let isComplicatedRule = RulesetUtils.containsComplicatedRule(rules);

    // check if ruleset itself has negation then it will be also complicated rule
    if (isComplicatedRule === false) {
      isComplicatedRule = RulesetUtils.containsNegation(ruleset);
    }
    return isComplicatedRule;
  }

  static extractConditionExpression(nodeId: string, ruleset: QueryNode, addNullCheck?: boolean): string {
    return RulesetUtils.extractConditionExpressionWithParser(nodeId, ruleset, RulesetUtils.parseRuleWrapValueSet,
      RulesetUtils.defaultConditionProvider, true, addNullCheck);
  }

  static generateDataExistenceConditionExpression(nodeId: string, ruleset: QueryNode, exist: boolean): string {
    const rules = RulesetUtils.extractRules(nodeId, ruleset);
    const uniqRules = uniqBy(rules, 'key');
    const uniqRulesRuleset = { type: undefined, condition: ruleset.condition, rules: uniqRules, negate: false};
    return exist ? RulesetUtils.extractConditionExpressionWithParser(nodeId, uniqRulesRuleset, RulesetUtils.parseRuleToExist, RulesetUtils.dataExistConditionProvider) :
      RulesetUtils.extractConditionExpressionWithParser(nodeId, uniqRulesRuleset, RulesetUtils.parseRuleToIsEMpty, RulesetUtils.dataNotExistConditionProvider);
  }

  static generateDataExistenceConditionExpressionForRule(nodeId: string, rule: Rule, exist: boolean): string {
    return exist ? RulesetUtils.parseRuleToExist(rule) : RulesetUtils.parseRuleToIsEMpty(rule);
  }

  public static parseRule(rule: Rule, wrap?: boolean): string {
    const operator = RulesetUtils.parseOperator(rule.operator);
    const value = RulesetUtils.parseValue(rule, wrap);
    return `${rule.fullName} ${operator} ${value}`;
  }

  public static parseRuleWrapValueSet(rule: Rule): string {
    return RulesetUtils.parseRule(rule, true);
  }

  private static parseOperator(operator: string) {
    switch (operator) {
      case '=':
        return RulesetUtils.equal;
      default:
        return operator;
    }
  }

  public static parseValue(rule: Rule, wrap?: boolean) {
    switch (rule.valueType) {
      case 'valueSet':
        const val = rule.value.code ? `${rule.value.code}` : `${rule.value.id}`;
        return !!wrap ? `\'${val}\'` : val;
      case 'string':
        return (wrap && rule.value) ? `'${rule.value}'` : `${rule.value}`;
      default:
        return `${rule.value}`;
    }
  }

  private static parseRuleToExist(rule: Rule): string {
    return `${rule.fullName} ${RulesetUtils.notEqual} null`;
  }

  public static parseRuleToIsEMpty(rule: Rule): string {
    return `${rule.fullName} ${RulesetUtils.equal} null`;
  }

  private static defaultConditionProvider(condition: Condition) {
    switch (condition) {
      case 'or':
        return RulesetUtils.or;
      case 'and':
        return RulesetUtils.and;
      default:
        return condition;
    }
  }

  private static dataExistConditionProvider(condition: Condition) {
    return RulesetUtils.and;
  }

  private static dataNotExistConditionProvider(condition: Condition) {
    return RulesetUtils.or;
  }

    public static wrapConditionWithManualSelectLogic(condition: string, manualSelect: Rule, noManualSelect: string) {
        return `(${noManualSelect} ${RulesetUtils.and} (${condition})) `
            + ` ${RulesetUtils.or} ${RulesetUtils.parseRule(manualSelect, true)}`;

    }

    private static createManualSelectCondition(nodeId: string, incomingGatewayId: string): Rule {
        const manualSelectConditionName = RulesetUtils.getManualSelectConditionName(incomingGatewayId);
        const rule: Rule = <Rule> {
            fullName: manualSelectConditionName,
            field: manualSelectConditionName,
            valueType: 'string',
            operator: '==',
            value: nodeId
        };
        return rule;
    }

    private static getManualSelectConditionName(incomingGatewayId: string): string {
        return `${incomingGatewayId}_manual_selection`;
    }

    public static addManualSelectCondition(nodeCreationResult: NodeCreationResult, nodeId: string, incomingGatewayId: string): Rule {
        const rule: Rule = RulesetUtils.createManualSelectCondition(nodeId, incomingGatewayId);
        nodeCreationResult.manualSelectNode = rule;
        return rule;
    }
}


function instanceOfRuleset(object: any): object is QueryNode {
  return 'condition' in object;
}
